const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function getInvitationState(invitation, now = new Date()) {
  const status = invitation?.status;
  if (status === 'pending') {
    const exp = new Date(invitation.expires_at);
    if (!Number.isNaN(exp.getTime()) && exp < now) return 'expired';
    return 'pending';
  }
  if (status === 'accepted' || status === 'revoked') return status;
  return 'pending';
}

export function formatExpiry(expiresAt, now = new Date()) {
  const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
  if (Number.isNaN(expiry)) return { text: 'No expiry', tone: 'ok' };
  const diff = expiry - now.getTime();
  if (diff >= 0) {
    const days = Math.floor(diff / DAY);
    if (days === 0) return { text: 'Expires today', tone: 'warn' };
    return {
      text: `Expires in ${plural(days, 'day')}`,
      tone: days <= 2 ? 'warn' : 'ok',
    };
  }
  const days = Math.floor(-diff / DAY);
  if (days === 0) return { text: 'Expired today', tone: 'danger' };
  return { text: `Expired ${plural(days, 'day')} ago`, tone: 'danger' };
}

export function formatRelativeTime(value, now = new Date()) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  const diff = now.getTime() - date.getTime();
  if (diff < MINUTE) return 'Just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
  if (diff < DAY) return `${plural(Math.floor(diff / HOUR), 'hour')} ago`;
  if (diff < 30 * DAY) return `${plural(Math.floor(diff / DAY), 'day')} ago`;
  return date.toLocaleDateString();
}

export function getDisplayName(user) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  return name || user?.email || 'Unknown';
}

export function getInitials(user) {
  const parts = [user?.first_name, user?.last_name].filter(Boolean);
  if (parts.length) {
    return parts.map((p) => p.trim()[0]).join('').slice(0, 2).toUpperCase();
  }
  if (user?.email) return user.email[0].toUpperCase();
  return '?';
}

export function filterMembers(users, { query = '', role = 'all', status = 'all' } = {}) {
  const q = query.trim().toLowerCase();
  return (users || []).filter((u) => {
    if (q) {
      const hay = `${getDisplayName(u)} ${u.email || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (role !== 'all' && u.role !== role) return false;
    if (status === 'active' && !u.is_active) return false;
    if (status === 'inactive' && u.is_active) return false;
    return true;
  });
}

const sortValue = (user, key) => {
  switch (key) {
    case 'name':
      return user.first_name || user.last_name ? getDisplayName(user).toLowerCase() : null;
    case 'role':
      return user.role ? String(user.role).toLowerCase() : null;
    case 'status':
      return user.is_active ? 1 : 0;
    case 'last_login':
    case 'created_at': {
      const t = user[key] ? new Date(user[key]).getTime() : NaN;
      return Number.isNaN(t) ? null : t;
    }
    default:
      return null;
  }
};

export function sortMembers(users, key, direction) {
  const sign = direction === 'descend' ? -1 : 1;
  return [...(users || [])].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (va < vb) return -sign;
    if (va > vb) return sign;
    return 0;
  });
}

export function filterInvitations(invitations, tab, now = new Date()) {
  if (tab === 'all') return invitations || [];
  return (invitations || []).filter((i) => getInvitationState(i, now) === tab);
}
